import PropTypes from 'prop-types';
import React, { Component } from 'react';
import classNames from 'classnames';
import styled from 'styled-components';

import Icon from 'components/Icon';
import { Keys } from 'react-keydown';
import { COLORS } from 'config/constants';
import { generateSelectorsFromTestIds } from 'helpers';

const StyledError = styled.div.attrs(() => ({
  className: 'mt-1'
}))`
  color: ${COLORS.DANGER};

  a {
    color: ${COLORS.DANGER};
    font-weight: 600;
    line-height: 1.4;
  }
`;

/**
 * [Input] - controlled component using standard styling.  To use with Redux, reach for InputReduxForm.
 * @param {string} name REQUIRED
 * @param {string} value REQUIRED
 * @param {string} type (defaults to 'text')
 * @param {string} [label]
 * @param {func} [renderFooter] - informational text to display below the input
 * @param {bool} [dark] - switch between white/dark input backgrounds
 * @param {bool} [large] - forces rounded corners for elements like search inputs
 * @param {bool | node} [error] - can pass a string, react node, or boolean. If true, error class will be applied
 * but no message will be displayed. Otherwise if node the error will be rendered below the input
 */

class Input extends Component {
  constructor(props) {
    super(props);

    this._hasClearProps = props.onClear || props.clearable;
    this.Input = React.createRef();
  }

  _onKeyDown = event => {
    if (this._hasClearProps && event.keyCode === Keys.ESC) {
      event.preventDefault();
      this._handleTextClear();
      this.Input.current.blur();
    }
  };

  _handleTextClear = () => {
    const { onChange, onClear } = this.props;

    if (onClear) return onClear();

    // simulate change event with empty value
    return onChange({
      target: { value: '' },
      currentTarget: { value: '' }
    });
  };

  render() {
    const {
      name,
      type = 'text',
      value,
      className,
      label,
      error,
      placeholder,
      dark,
      disabled,
      large,
      renderFooter,
      rounded,
      prependIcon,
      // ////// Extract props we don't want to pass to input
      onClear,
      clearable,
      autoComplete,
      // ////// END
      ...rest
    } = this.props;

    const isClearable = this._hasClearProps && value.length > 0;

    const _wrapperClass = classNames('Input__container', 'form-group', className, {
      'FormElement--light': !dark,
      // Dark currenlty is used, but is a placeholder for using common component for light/dark input styles.
      'FormElement--dark': dark,
      'FormElement--large': large,
      'FormElement--with-icon-prepend': prependIcon,
      'FormElement--with-icon-append': isClearable,
      'FormElement--rounded': rounded
    });

    const id = rest.id || name;

    return (
      <div className={_wrapperClass}>
        {label && <label htmlFor={id}>{label}</label>}

        <div className="Input__container__inner">
          {prependIcon && (
            <div className="Input__icon Input__icon--prepend">{prependIcon}</div>
          )}

          <input
            id={id}
            ref={this.Input}
            type={type}
            name={name}
            className={classNames('form-control', {
              'Input--error': error
            })}
            placeholder={placeholder}
            disabled={disabled}
            value={value}
            onKeyDown={this._onKeyDown}
            autoComplete={autoComplete ? 'on' : 'off'}
            {...rest}
          />

          {isClearable && (
            <div
              className="Input__icon Input__icon--append"
              onClick={() => {
                this._handleTextClear();
                // Refocus on input since clicking on element outside of input field
                this.Input.current.focus();
              }}
              role="button"
              tabIndex="0"
            >
              <Icon type={Icon.TYPES.CLEAR} width={14} height={14} />
            </div>
          )}
        </div>

        {(typeof error === 'string' || React.isValidElement(error)) && (
          <StyledError data-testid={Input.TEST_IDS.ERROR_MESSAGE}>{error}</StyledError>
        )}
        {renderFooter && <div className="Input__footer">{renderFooter()}</div>}
      </div>
    );
  }
}

Input.TEST_IDS = {
  ERROR_MESSAGE: 'Input-error'
};

Input.SELECTORS = generateSelectorsFromTestIds(Input.TEST_IDS);

Input.propTypes = {
  className: PropTypes.string,
  clearable: PropTypes.bool,
  dark: PropTypes.bool,
  disabled: PropTypes.bool,
  rounded: PropTypes.bool,
  prependIcon: PropTypes.node,
  type: PropTypes.oneOf(['text']),
  name: PropTypes.string,
  value: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.node, PropTypes.bool]),
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  large: PropTypes.bool,
  onClear: PropTypes.func,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  renderFooter: PropTypes.func,
  autoComplete: PropTypes.bool
};

export default Input;
